import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Bot, Info, FileText, Shield, Cookie } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";

// Legal content
const LEGAL_CONTENT = {
  terms: {
    title: "Terms of Service",
    content: `Last Updated: December 2025

1. ACCEPTANCE OF TERMS

By accessing or using Unizar ("Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service.

2. DESCRIPTION OF SERVICE

Unizar provides an AI-powered business analysis platform featuring "Zoey," an AI Business Analyst that helps identify business pain points and recommend solutions. The Service includes:
- AI-powered chat conversations
- Business analysis and recommendations
- Lead management and CRM features
- AI-assisted communication tools (calls, emails, texts)

3. USER ACCOUNTS

You must create an account to access certain features. You are responsible for:
- Maintaining the confidentiality of your account credentials
- All activities that occur under your account
- Providing accurate and complete information

4. ACCEPTABLE USE

You agree not to:
- Use the Service for any unlawful purpose
- Attempt to gain unauthorized access to the Service
- Interfere with or disrupt the Service
- Upload malicious code or content
- Impersonate others or provide false information

5. AI INTERACTIONS

When using our AI features (Zoey chat, AI calls, AI emails):
- AI responses are generated based on your inputs and may not always be accurate
- You should verify important business decisions independently
- AI call recordings may be stored for quality and training purposes
- Message history is retained to provide continuous service

6. PAYMENT TERMS

- Free tier users have limited access to features
- Pro and Plus subscribers are billed according to their selected plan
- Payments are processed securely through Stripe
- Refunds are handled on a case-by-case basis

7. INTELLECTUAL PROPERTY

All content, features, and functionality of the Service are owned by Unizar and protected by intellectual property laws. You retain ownership of content you submit but grant us license to use it for providing the Service.

8. LIMITATION OF LIABILITY

Unizar is provided "as is" without warranties. We are not liable for any indirect, incidental, or consequential damages arising from your use of the Service.

9. TERMINATION

We may terminate or suspend your account at any time for violations of these terms. You may delete your account at any time through your account settings.

10. CHANGES TO TERMS

We reserve the right to modify these terms at any time. Continued use of the Service constitutes acceptance of updated terms.

11. CONTACT

For questions about these Terms, contact us at biz@unizar.ai`
  },
  privacy: {
    title: "Privacy Policy",
    content: `Last Updated: December 2025

1. INTRODUCTION

Unizar ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.

2. INFORMATION WE COLLECT

Personal Information:
- Name, email address, and contact details
- Account credentials (securely hashed)
- Billing information (processed by Stripe)
- Business information you provide to Zoey

Usage Data:
- Chat conversations and message history
- AI call recordings and transcripts
- Feature usage patterns
- Device and browser information

3. HOW WE USE YOUR INFORMATION

We use collected information to:
- Provide and maintain the Service
- Process transactions and send related information
- Improve our AI models and Service quality
- Send you updates, marketing, and promotional materials (with consent)
- Respond to your comments, questions, and requests
- Monitor and analyze usage patterns

4. AI DATA PROCESSING

Our AI features process your data as follows:
- Chat Messages: Analyzed by AI to provide business recommendations
- AI Calls: Recorded, transcribed, and analyzed for lead management
- AI Emails/Texts: Generated based on conversation context

You can request deletion of your AI interaction data at any time.

5. DATA SHARING

We may share your information with:
- Service providers (Stripe for payments, Retell.ai for calls)
- Analytics providers to improve our Service
- Law enforcement when required by law

We do NOT sell your personal information to third parties.

6. DATA SECURITY

We implement appropriate security measures including:
- Encryption of data in transit and at rest
- Secure authentication mechanisms
- Regular security assessments
- Access controls and monitoring

7. DATA RETENTION

We retain your data for as long as your account is active or as needed to provide services. You can request data deletion through your account settings or by contacting us.

8. YOUR RIGHTS

Depending on your location, you may have rights to:
- Access your personal data
- Correct inaccurate data
- Delete your data
- Export your data
- Opt out of marketing communications

9. COOKIES AND TRACKING

We use cookies and similar technologies for:
- Session management
- Remembering preferences
- Analytics and performance monitoring

See our Cookie Policy for more details.

10. CHILDREN'S PRIVACY

The Service is not intended for users under 18. We do not knowingly collect information from children.

11. INTERNATIONAL TRANSFERS

Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place.

12. CHANGES TO THIS POLICY

We may update this Privacy Policy periodically. We will notify you of material changes via email or Service notification.

13. CONTACT US

For privacy-related inquiries:
Email: biz@unizar.ai`
  },
  cookies: {
    title: "Cookie Policy",
    content: `Last Updated: December 2025

1. WHAT ARE COOKIES?

Cookies are small text files stored on your device when you visit a website. They help websites remember your preferences and improve your experience.

2. HOW WE USE COOKIES

Unizar uses cookies for the following purposes:

Essential Cookies:
- Authentication and session management
- Security features
- Load balancing

These cookies are necessary for the Service to function and cannot be disabled.

Functional Cookies:
- Remembering your preferences
- Storing your theme settings
- Maintaining chat context

Performance Cookies:
- Understanding how you use our Service
- Identifying errors and issues
- Measuring page load times

Analytics Cookies:
- Tracking usage patterns
- Measuring feature adoption
- Improving Service quality

3. THIRD-PARTY COOKIES

We use services that may set their own cookies:
- Stripe: Payment processing
- Analytics providers: Usage tracking

These third parties have their own privacy policies governing cookie use.

4. COOKIE DURATION

Session Cookies: Deleted when you close your browser
Persistent Cookies: Remain on your device for a set period or until you delete them

5. MANAGING COOKIES

You can control cookies through your browser settings:
- Block all cookies
- Delete existing cookies
- Allow only certain cookies

Note: Blocking essential cookies may prevent the Service from functioning properly.

Browser-specific instructions:
- Chrome: Settings > Privacy and Security > Cookies
- Firefox: Options > Privacy & Security > Cookies
- Safari: Preferences > Privacy > Cookies
- Edge: Settings > Privacy > Cookies

6. DO NOT TRACK

Some browsers have a "Do Not Track" feature. We currently do not respond to DNT signals, but you can manage your preferences through cookie settings.

7. UPDATES TO THIS POLICY

We may update this Cookie Policy as our practices change. Check back periodically for updates.

8. CONTACT US

Questions about our cookie practices?
Email: biz@unizar.ai`
  }
};

// ZOEY's welcome message for new visitors
const ZOEY_WELCOME_MESSAGE = `Hey there — welcome to Unizar. I'm Zoey, and I'm here to help.

Unizar specializes in helping businesses streamline their operations and eliminate pain points through smart, technology-driven solutions.

To get started and see how we can make a real impact for you, go ahead and click the **Get Started** tab to create your free account — and then we can dive in together.`;

export default function Home() {
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [showWelcome, setShowWelcome] = useState(false);
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [legalModal, setLegalModal] = useState({ open: false, type: null });
  const textareaRef = useRef(null);

  // Typewriter effect for ZOEY's welcome message
  useEffect(() => {
    // Show welcome message after a short delay
    const showTimer = setTimeout(() => {
      setShowWelcome(true);
      setIsTyping(true);
    }, 500);

    return () => clearTimeout(showTimer);
  }, []);

  // Typewriter animation
  useEffect(() => {
    if (!showWelcome || !isTyping) return;

    let currentIndex = 0;
    const typeInterval = setInterval(() => {
      if (currentIndex < ZOEY_WELCOME_MESSAGE.length) {
        setDisplayedText(ZOEY_WELCOME_MESSAGE.slice(0, currentIndex + 1));
        currentIndex++;
      } else {
        setIsTyping(false);
        clearInterval(typeInterval);
      }
    }, 15); // Typing speed

    return () => clearInterval(typeInterval);
  }, [showWelcome, isTyping]);

  const handleInputChange = (e) => {
    setInput(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 300)}px`;
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleGo();
    }
  };

  const handleGo = () => {
    if (input.trim()) {
      // Store the initial message and redirect to signup
      sessionStorage.setItem("initialMessage", input.trim());
      navigate("/signup");
    }
  };

  // Format message with bold text and clickable "Get Started" link
  const formatMessage = (text) => {
    // Replace **text** with bold spans, and make "Get Started" a clickable link
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const innerText = part.slice(2, -2);
        // Make "Get Started" a clickable link
        if (innerText === "Get Started") {
          return (
            <a 
              key={index} 
              href="/signup"
              className="font-semibold text-[#14A800] hover:underline cursor-pointer"
            >
              {innerText}
            </a>
          );
        }
        return (
          <span key={index} className="font-semibold text-[#14A800]">
            {innerText}
          </span>
        );
      }
      return part;
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#0D0D0D]">
      {/* Header */}
      <header className="flex items-center justify-between px-8 py-5">
        <div className="flex items-center">
          <img src="/logo.png" alt="Unizar" className="h-10 w-auto" />
        </div>
        <div className="flex items-center gap-8">
          <a 
            href="/login" 
            className="text-white text-sm hover:opacity-80 transition-opacity"
            data-testid="login-link"
          >
            Log In
          </a>
          <a 
            href="/signup" 
            className="text-white text-sm hover:opacity-80 transition-opacity bg-[#14A800] hover:bg-[#108A00] px-4 py-2 rounded-full"
            data-testid="get-started-link"
          >
            Get Started
          </a>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="text-[#888888] hover:text-white transition-colors p-2 rounded-full hover:bg-[#1a1a1a]"
                data-testid="info-dropdown-trigger"
              >
                <Info className="w-5 h-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              align="end" 
              className="bg-[#1a1a1a] border-[#2a2a2a] text-white min-w-[180px]"
            >
              <DropdownMenuItem 
                onClick={() => setLegalModal({ open: true, type: 'terms' })}
                className="cursor-pointer hover:bg-[#2a2a2a] focus:bg-[#2a2a2a] focus:text-white"
                data-testid="terms-of-service-item"
              >
                <FileText className="w-4 h-4 mr-2 text-[#14A800]" />
                Terms of Service
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setLegalModal({ open: true, type: 'privacy' })}
                className="cursor-pointer hover:bg-[#2a2a2a] focus:bg-[#2a2a2a] focus:text-white"
                data-testid="privacy-policy-item"
              >
                <Shield className="w-4 h-4 mr-2 text-[#14A800]" />
                Privacy Policy
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setLegalModal({ open: true, type: 'cookies' })}
                className="cursor-pointer hover:bg-[#2a2a2a] focus:bg-[#2a2a2a] focus:text-white"
                data-testid="cookie-policy-item"
              >
                <Cookie className="w-4 h-4 mr-2 text-[#14A800]" />
                Cookie Policy
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Divider line */}
      <div className="w-full h-px bg-gradient-to-r from-transparent via-[#14A800] to-transparent opacity-50" />

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 py-8">
        <div className="w-full max-w-2xl">
          {/* ZOEY's Welcome Message */}
          {showWelcome && (
            <div 
              className="mb-8 animate-fade-in"
              data-testid="zoey-welcome"
            >
              <div className="flex items-start gap-4">
                {/* ZOEY Avatar */}
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#14A800] to-[#0D7A00] flex items-center justify-center shadow-lg shadow-[#14A800]/20">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                
                {/* Message Content */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[#14A800] font-medium text-sm">Zoey</span>
                    <span className="text-[#555555] text-xs">AI Business Analyst</span>
                  </div>
                  <div className="bg-[#1a1a1a] rounded-2xl rounded-tl-md p-5 text-[#EDEDED] leading-relaxed whitespace-pre-line">
                    {formatMessage(displayedText)}
                    {isTyping && (
                      <span className="inline-block w-2 h-4 bg-[#14A800] ml-1 animate-pulse" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Input area */}
          <div className="w-full">
            <div className="relative bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] focus-within:border-[#14A800]/50 transition-colors">
              <Textarea
                ref={textareaRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                placeholder="Tell us what problem you are trying to solve..."
                className="min-h-[120px] max-h-[300px] bg-transparent border-none resize-none py-5 px-5 pr-20 text-white text-base placeholder:text-[#555555] focus:ring-0 rounded-xl"
                data-testid="home-input"
              />
              <Button
                onClick={handleGo}
                disabled={!input.trim()}
                className="absolute right-4 bottom-4 w-12 h-12 p-0 bg-[#14A800] hover:bg-[#108A00] disabled:bg-[#333333] disabled:opacity-50 rounded-full transition-colors"
                data-testid="go-btn"
              >
                <span className="text-white font-medium text-sm">Go</span>
              </Button>
            </div>
            <p className="text-[#555555] text-xs mt-3 text-center">
              Create a free account to start working with Zoey
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-6 text-center">
        <p className="text-[#555555] text-sm">
          Powered by Unizar
        </p>
      </footer>

      {/* Legal Content Modal */}
      <Dialog open={legalModal.open} onOpenChange={(open) => setLegalModal({ open, type: open ? legalModal.type : null })}>
        <DialogContent className="bg-[#1a1a1a] border-[#2a2a2a] text-white max-w-2xl max-h-[80vh]" data-testid="legal-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-[#14A800]" data-testid="legal-modal-title">
              {legalModal.type && LEGAL_CONTENT[legalModal.type]?.title}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[60vh] pr-4">
            <div className="text-[#EDEDED] text-sm leading-relaxed whitespace-pre-line" data-testid="legal-modal-content">
              {legalModal.type && LEGAL_CONTENT[legalModal.type]?.content}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
